import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, Calendar, X, Loader2, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

export default function SpeedMeetingBooking({ eventId, slot, user, onClose, onBooked }) {
  const [view, setView] = useState("exhibitors"); // exhibitors | contacts
  const [exhibitors, setExhibitors] = useState([]);
  const [selectedExh, setSelectedExh] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/events/${eventId}/agenda/${slot.id}/exhibitors`).then((r) => {
      setExhibitors(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [eventId, slot.id]);

  const openExhibitor = async (exh) => {
    setSelectedExh(exh);
    setView("contacts");
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}/agenda/${slot.id}/exhibitors/${exh.id}/availability`);
      setAvailability(data);
    } catch (err) { toast.error("Impossible de charger les disponibilités"); }
    setLoading(false);
  };

  const book = async (contactId, subIdx) => {
    if (!user) { toast.error("Connectez-vous pour réserver"); return; }
    setBookingLoading(true);
    try {
      await api.post(`/events/${eventId}/agenda/${slot.id}/meetings`, {
        exhibitor_id: selectedExh.id,
        contact_user_id: contactId,
        sub_slot_index: subIdx,
      });
      toast.success("RDV confirmé !");
      // Refresh availability
      const { data } = await api.get(`/events/${eventId}/agenda/${slot.id}/exhibitors/${selectedExh.id}/availability`);
      setAvailability(data);
      onBooked?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBookingLoading(false);
  };

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="sm-booking-modal">
      <button onClick={onClose} className="absolute inset-0 bg-neutral-900/70 backdrop-blur-sm" aria-label="Fermer" />
      <div className="relative max-h-[88vh] w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-center gap-3 border-b border-neutral-100 px-6 py-4">
          {view === "contacts" && <button onClick={() => { setView("exhibitors"); setSelectedExh(null); }} className="text-sm font-semibold text-wine-600 hover:text-wine-700">← Retour</button>}
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-xl font-medium text-neutral-900 truncate">{view === "exhibitors" ? slot.title : selectedExh?.company_name}</h2>
            <p className="text-xs text-neutral-500">{view === "exhibitors" ? `${exhibitors.length} exposant(s) disponibles` : "Choisissez un contact et un créneau"}</p>
          </div>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" data-testid="sm-modal-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-wine-500" /></div> : view === "exhibitors" ? (
            exhibitors.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-neutral-200 p-10 text-center text-sm text-neutral-500" data-testid="sm-no-exhibitors">Aucun exposant inscrit pour ce créneau. Revenez plus tard.</div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2" data-testid="sm-exhibitors-list">
                {exhibitors.map((e) => (
                  <button key={e.id} onClick={() => openExhibitor(e)} className="flex items-start gap-3 rounded-2xl border border-neutral-100 bg-white p-4 text-left transition-all hover:border-wine-300 hover:shadow-md" data-testid={`sm-exh-${e.id}`}>
                    {e.company_logo ? <img src={resolveUrl(e.company_logo)} alt="" className="h-12 w-12 rounded-xl object-contain bg-white p-1" /> : <div className="grid h-12 w-12 place-items-center rounded-xl bg-wine-100"><Building2 className="h-5 w-5 text-wine-600" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-navy truncate">{e.company_name}</div>
                      {e.role && <span className="mt-1 inline-block rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-semibold text-wine-700">{e.role}</span>}
                      {e.booth_number && <span className="ml-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-600">Stand {e.booth_number}</span>}
                      {e.description && <p className="mt-1.5 text-xs text-neutral-600 line-clamp-2">{e.description}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )
          ) : (
            availability?.contacts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-neutral-200 p-10 text-center text-sm text-neutral-500">Aucun contact disponible pour cet exposant.</div>
            ) : (
              <div className="space-y-5" data-testid="sm-contacts-list">
                {availability?.contacts.map((c) => (
                  <div key={c.id} className="rounded-2xl border border-neutral-100 bg-white p-4">
                    <div className="flex items-center gap-3">
                      {c.photo ? <img src={resolveUrl(c.photo)} alt="" className="h-10 w-10 rounded-full object-cover" /> : <div className="grid h-10 w-10 place-items-center rounded-full bg-wine-100 text-xs font-bold text-wine-700"><Users className="h-4 w-4" /></div>}
                      <div>
                        <div className="font-bold text-navy">{c.name}</div>
                        {c.job_title && <div className="text-xs text-neutral-500">{c.job_title}</div>}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-1.5 sm:grid-cols-3 md:grid-cols-4">
                      {c.slots.map((s) => (
                        <button key={s.index} disabled={!s.available || bookingLoading} onClick={() => book(c.id, s.index)} className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-all ${s.available ? "border-wine-200 bg-white text-wine-700 hover:border-wine-500 hover:bg-wine-50" : "border-neutral-200 bg-neutral-100 text-neutral-400 cursor-not-allowed line-through"}`} data-testid={`sm-slot-${c.id}-${s.index}`}>
                          <Calendar className="mr-1 inline h-3 w-3" /> {fmtTime(s.start)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        <div className="border-t border-neutral-100 bg-neutral-50/60 px-6 py-3 text-[11px] text-neutral-500 text-center">
          {user ? <CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-500" /> : null}
          {user ? `Connecté en tant que ${user.name}` : <>Connectez-vous pour réserver — <Link to="/login" className="font-semibold text-wine-700 hover:underline">Se connecter</Link></>}
        </div>
      </div>
    </div>
  );
}
